/**
 * BetSlipExportCard — render-target for image export.
 *
 * Two modes:
 *   • mode="ronin": render a single multipla (generated by Ronin) as a
 *     square card with combined odds, target return, and a styled list
 *     of leg picks.
 *   • mode="bets":  render a "I miei pronostici" group for a specific
 *     day, listing each match with its bets and outcome badges.
 *
 * Built to match the app's "Dark Futuristic / Neon Glow" aesthetic so
 * exported images look like an extension of the product, not a generic
 * spreadsheet dump.
 *
 * Designed for 1080×1350 (Instagram 4:5) export. Render off-screen via
 * absolute positioning; the parent decides when to capture it via
 * ``exportNodeToPng``.
 */
import React from "react";

const C = {
  bg: "var(--bc-bg, #020202)", surface: "#0B0B0D", card: "#0F0F11",
  border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)",
  green: "#22C55E", red: "#EF4444", cyan: "#06B6D4", amber: "#F59E0B",
};
const NEON = "#22FFA3";
// Neutral grey used for "pending / not graded yet" — keeps the card calm
// before a match finishes, so green/red are reserved for actual outcomes.
const GREY = "#52525B";

const fmtDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
};

const Header = () => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "32px 40px 18px", borderBottom: `1px solid ${C.border}`,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{
        width: 38, height: 38, borderRadius: 8,
        background: `radial-gradient(circle at 30% 30%, ${NEON}, #0a3d27)`,
        boxShadow: `0 0 22px ${NEON}88`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Barlow Condensed',sans-serif", fontSize: 22, fontWeight: 800, color: "#020202",
      }}>BC</div>
      <div>
        <div style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontSize: 22, fontWeight: 800,
          letterSpacing: "0.18em", color: C.text, lineHeight: 1,
        }}>BET <span style={{ color: NEON }}>CRACKER</span></div>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: 11,
          color: C.muted, marginTop: 4, letterSpacing: "0.12em",
        }}>betcracker.it</div>
      </div>
    </div>
    <div style={{
      fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.muted, letterSpacing: "0.12em",
    }}>
      {new Date().toLocaleDateString("it-IT")}
    </div>
  </div>
);

const Footer = ({ note }) => (
  <div style={{
    padding: "16px 40px", borderTop: `1px solid ${C.border}`,
    fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: C.muted,
    display: "flex", justifyContent: "space-between", letterSpacing: "0.1em",
  }}>
    <span>{note || "Pronostico personale · gioco responsabilmente"}</span>
    <span>+18 · ADM · #betcracker</span>
  </div>
);

/* ─── RONIN MULTIPLA ────────────────────────────────────────────────── */
function RoninExport({ slip }) {
  const legs = slip?.legs || [];
  const combined = slip?.combined_odds;
  const prob = slip?.combined_prob;
  const label = slip?.coach_label || (slip?.coach || "").toUpperCase();
  const stake = slip?.stake || 10;
  const win = combined ? (stake * combined).toFixed(2) : "—";
  const status = slip?.status; // "won" | "lost" | "pending"
  const hasResults = legs.some((l) => l.result);
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 1350 }}>
      <Header />
      {/* Title */}
      <div style={{ padding: "22px 40px 6px" }}>
        <div style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontSize: 14,
          color: NEON, letterSpacing: "0.2em", fontWeight: 700,
        }}>RONIN MULTIPLA · COACH {label}</div>
        <div style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontSize: 38, fontWeight: 800,
          color: C.text, letterSpacing: "-0.01em", marginTop: 2,
        }}>
          Quota <span style={{ color: NEON, textShadow: `0 0 14px ${NEON}` }}>×{combined?.toFixed(2)}</span>
        </div>
        <div style={{
          fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: C.muted, marginTop: 4,
        }}>
          {legs.length} leg · probabilità combinata {prob?.toFixed(1)}%
          {hasResults && (status === "won" ? "  ·  ✅ VINTA" : status === "lost" ? "  ·  ❌ PERSA" : "")}
        </div>
      </div>

      {/* Stake / Win row */}
      <div style={{
        margin: "18px 40px 0", padding: "14px 18px",
        background: C.card, border: `1px solid ${C.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: C.muted, letterSpacing: "0.15em" }}>STAKE</div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 22, color: C.text, fontWeight: 600 }}>€{stake}</div>
        </div>
        <div style={{ height: 32, width: 1, background: C.border }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: C.muted, letterSpacing: "0.15em" }}>VINCITA</div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 22, color: NEON, fontWeight: 600, textShadow: `0 0 8px ${NEON}66` }}>€{win}</div>
        </div>
      </div>

      {/* Legs */}
      <div style={{ flex: 1, padding: "20px 40px 8px" }}>
        {legs.map((leg, i) => {
          const r = leg.result; // "won" | "lost" | "pending" | undefined
          // Grey when the leg has not been graded yet — only flip to green
          // or red once the match is finished.
          const accent = r === "won" ? C.green : r === "lost" ? C.red : GREY;
          const dense = legs.length > 8;
          return (
            <div key={i} style={{
              padding: dense ? "6px 0" : "10px 0",
              borderBottom: i === legs.length - 1 ? "none" : `1px solid ${C.border}`,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{
                width: 22, height: 22, flexShrink: 0,
                background: `${accent}1A`, border: `1px solid ${accent}55`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: accent, fontWeight: 700,
              }}>{i + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: 13,
                  color: C.text, fontWeight: 600, lineHeight: 1.3,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {leg.home_team?.name || leg.home || "Casa"} vs {leg.away_team?.name || leg.away || "Ospite"}
                </div>
                <div style={{
                  fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.muted, marginTop: 2,
                }}>
                  {leg.label || leg.bet} · {leg.prob}%
                </div>
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono',monospace", fontSize: 14, color: accent,
                fontWeight: 600, flexShrink: 0,
              }}>
                ×{leg.odds?.toFixed(2)}
              </div>
            </div>
          );
        })}
      </div>
      <Footer note={hasResults ? `Pronostico ${status === "won" ? "vinto" : status === "lost" ? "perso" : "in attesa"}` : null} />
    </div>
  );
}

/* ─── MIE PRONOSTICI · gruppo per giorno ─────────────────────────────── */
function BetsGroupExport({ date, bets, results }) {
  const finished = bets.filter((b) => results?.[b.fixture_id]?.status && results[b.fixture_id].status !== "pending").length;
  const correct = bets.filter((b) => results?.[b.fixture_id]?.combo_badge === "green").length;
  const dense = bets.length > 7;
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 1350 }}>
      <Header />
      <div style={{ padding: "26px 44px 8px" }}>
        <div style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontSize: 15,
          color: NEON, letterSpacing: "0.22em", fontWeight: 700,
          textShadow: `0 0 12px ${NEON}66`,
        }}>
          I MIEI PRONOSTICI
        </div>
        <div style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontSize: 44, fontWeight: 800,
          color: C.text, marginTop: 4, lineHeight: 1.05,
        }}>
          {fmtDate(date)}
        </div>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: 13, color: C.muted,
          marginTop: 8, letterSpacing: "0.05em",
        }}>
          {bets.length} {bets.length === 1 ? "PARTITA SALVATA" : "PARTITE SALVATE"}
          {finished > 0 && (
            <span style={{ marginLeft: 12, color: NEON, textShadow: `0 0 8px ${NEON}88` }}>
              · {correct}/{finished} ESATTE
            </span>
          )}
        </div>
      </div>
      <div style={{ flex: 1, padding: "16px 44px 12px" }}>
        {bets.map((sel, i) => {
          const r = results?.[sel.fixture_id];
          const status = r?.combo_badge; // "green" | "red" | undefined
          const accent = status === "green" ? C.green : status === "red" ? C.red : GREY;
          const hasScore = r && r.score_home != null && r.score_away != null;
          // Per-leg badge lookup — backend ships it inside r.bets[].
          const legBadgeMap = {};
          for (const lb of r?.bets || []) {
            if (lb?.bet) legBadgeMap[lb.bet] = lb.badge || null;
          }
          return (
            <div key={sel.fixture_id} style={{
              padding: dense ? "12px 0 14px" : "18px 0 20px",
              borderBottom: i === bets.length - 1 ? "none" : `1px solid ${C.border}`,
              position: "relative",
            }}>
              {/* Left accent bar (neon glow on outcome) */}
              <div style={{
                position: "absolute", left: -12, top: dense ? 10 : 16, bottom: dense ? 10 : 16,
                width: 3, background: accent,
                boxShadow: hasScore ? `0 0 10px ${accent}` : "none",
              }} />

              {/* Top row: league meta + (final score if any) */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                marginBottom: 4,
              }}>
                <div style={{
                  fontFamily: "'JetBrains Mono',monospace", fontSize: 11,
                  color: C.muted, letterSpacing: "0.14em", textTransform: "uppercase",
                }}>
                  {[sel.country, sel.league].filter(Boolean).join(" · ") || "—"}
                </div>
                {hasScore && (
                  <div style={{
                    fontFamily: "'JetBrains Mono',monospace", fontSize: 18, fontWeight: 700,
                    color: accent, letterSpacing: "0.02em",
                    textShadow: `0 0 10px ${accent}88`,
                  }}>
                    {r.score_home}–{r.score_away}
                    <span style={{
                      marginLeft: 8, fontSize: 9, color: accent, opacity: 0.75,
                      letterSpacing: "0.18em",
                    }}>FT</span>
                  </div>
                )}
              </div>

              {/* Teams */}
              <div style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontSize: dense ? 24 : 28,
                color: C.text, fontWeight: 700, lineHeight: 1.15,
                letterSpacing: "0.01em",
                display: "flex", alignItems: "baseline", gap: 12,
                flexWrap: "wrap",
              }}>
                <span>{sel.home || sel.match?.split(" vs ")[0] || "Casa"}</span>
                <span style={{
                  fontFamily: "'JetBrains Mono',monospace", fontSize: 13,
                  color: C.muted, fontWeight: 400, letterSpacing: "0.16em",
                }}>VS</span>
                <span>{sel.away || sel.match?.split(" vs ")[1] || "Ospite"}</span>
              </div>

              {/* Bets list */}
              <div style={{
                marginTop: dense ? 8 : 12, display: "flex", flexDirection: "column",
                gap: dense ? 4 : 6,
              }}>
                {(sel.bets || []).map((b, j) => {
                  const fair = b.odds && b.odds > 1
                    ? Number(b.odds)
                    : b.prob ? (100 / b.prob) : null;
                  const lb = legBadgeMap[b.bet];
                  const lbColor = lb === "green" ? C.green : lb === "red" ? C.red : null;
                  const lbIcon = lb === "green" ? "✓" : lb === "red" ? "✗" : "•";
                  return (
                    <div key={j} style={{
                      display: "flex", alignItems: "center", gap: 14,
                      padding: dense ? "7px 12px" : "10px 14px",
                      background: lbColor ? `${lbColor}10` : "rgba(255,255,255,0.025)",
                      border: `1px solid ${lbColor ? `${lbColor}55` : C.border}`,
                      borderLeft: `3px solid ${lbColor || NEON}`,
                    }}>
                      {/* Outcome icon (left) */}
                      <div style={{
                        width: 22, height: 22, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: lbColor ? `${lbColor}25` : "transparent",
                        border: lbColor ? `1px solid ${lbColor}` : `1px dashed ${GREY}88`,
                        color: lbColor || GREY,
                        fontFamily: "'JetBrains Mono',monospace", fontSize: 13, fontWeight: 700,
                        borderRadius: 3,
                      }}>{lbIcon}</div>

                      {/* Bet label */}
                      <div style={{
                        flex: 1, minWidth: 0,
                        fontFamily: "'DM Sans',sans-serif", fontSize: dense ? 14 : 15,
                        color: C.text, fontWeight: 600, lineHeight: 1.25,
                      }}>{b.bet}</div>

                      {/* Prob */}
                      {b.prob ? (
                        <div style={{
                          fontFamily: "'JetBrains Mono',monospace", fontSize: 13,
                          color: C.muted, fontWeight: 600, flexShrink: 0,
                          letterSpacing: "0.02em",
                        }}>{Math.round(b.prob)}%</div>
                      ) : null}

                      {/* Odds */}
                      {fair ? (
                        <div style={{
                          fontFamily: "'JetBrains Mono',monospace", fontSize: dense ? 15 : 17,
                          color: NEON, fontWeight: 700, flexShrink: 0,
                          textShadow: `0 0 8px ${NEON}66`,
                          minWidth: 56, textAlign: "right",
                        }}>×{fair.toFixed(2)}</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <Footer />
    </div>
  );
}

/* ─── ROOT EXPORT CARD ────────────────────────────────────────────────── */
const BetSlipExportCard = React.forwardRef(function BetSlipExportCard({ mode, slip, date, bets, results }, ref) {
  return (
    <div ref={ref} style={{
      width: 1080, minHeight: 1350,
      background: `radial-gradient(ellipse at top, ${NEON}10, transparent 55%), ${C.bg}`,
      color: C.text,
      fontFamily: "'DM Sans',sans-serif",
      position: "relative", overflow: "hidden",
    }}>
      {mode === "ronin" && <RoninExport slip={slip} />}
      {mode === "bets"  && <BetsGroupExport date={date} bets={bets} results={results} />}
      {/* Decorative neon corner */}
      <div style={{
        position: "absolute", top: 0, right: 0, width: 200, height: 200, pointerEvents: "none",
        background: `radial-gradient(circle at 100% 0%, ${NEON}22, transparent 60%)`,
      }} />
    </div>
  );
});

export default BetSlipExportCard;
